import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import SimplifiedMic from '~/modules/game-engine/input/simplified-mic';
import { transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import usePermissions from '~/routes/remote-mic/hooks/use-permissions';
import RemoteMicKeyboard from '~/routes/remote-mic/panels/microphone/keyboard';
import { ConnectionStatuses } from '~/routes/remote-mic/remote-mic';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  isKeepAwakeOn: boolean;
  setIsKeepAwakeOn: (keepAwake: boolean) => void;
  monitoringStarted: boolean;
  setMonitoringStarted: (micMonitoring: boolean) => void;
  micMuted: boolean;
}

// The microphone tab: whatever screen the TV is on, mirrored. Whether the microphone is live is said
// by the top bar, which is on every tab.
function Microphone({ monitoringStarted, connectionStatus, micMuted }: Props) {
  const permissions = usePermissions();
  // The phone listens from the moment this tab is up, as it did when the level meter lived here - unless
  // it was turned off in Settings, which landscape remounting this tab on every visit must not undo.
  useEffect(() => {
    if (!micMuted) SimplifiedMic.startMonitoring();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isReconnecting = connectionStatus === 'reconnecting' || connectionStatus === 'disconnected';

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-5 pt-4 pb-3.5">
        {permissions === 'write' ? (
          <RemoteMicKeyboard isMicOn={monitoringStarted} />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3.5 pb-2 text-center">
            <Icon icon="ic:outline-lock" size={10} className="text-white/60" />
            <span className="text-[1.375rem] font-bold">This phone can&#39;t drive the game</span>
            <span data-test="no-permissions-message" className="text-[1rem] leading-[1.5] text-white/88">
              It still works as a microphone. To let it control the game, open{' '}
              <strong>Settings → Remote Microphones</strong> on the TV and give it write permission.
            </span>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isReconnecting && (
          <motion.div
            data-test="reconnecting"
            className="absolute inset-0 z-7 flex flex-col items-center justify-center gap-4.5 bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}>
            <span
              aria-hidden
              className="border-t-active size-13 animate-spin rounded-full border-[0.3125rem] border-white/20"
            />
            <span className="text-[1.1875rem] font-bold">Reconnecting…</span>
            <span className="text-[0.9375rem] text-white/85">Keep the phone on the same Wi-Fi.</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
export default Microphone;
